import {
  assertSafeIntegerInRange,
  RARITIES,
  type Rarity,
  SHIP_LEVEL_1_DAMAGE_BONUS_BPS,
} from '@kenocyber/shared';
import { MAX_SHIP_LEVEL } from './merge';

/**
 * Ships.
 *
 * A ship contributes **only** a percentage damage bonus. It never adds flat
 * damage, attack speed or health. Exactly one ship is equipped at a time.
 */

/** Two ships per rarity, ten rarities. */
export const SHIPS_PER_RARITY = 2;
export const INITIAL_SHIP_COUNT = SHIPS_PER_RARITY * RARITIES.length;

/**
 * Level-1 damage bonus per rarity, in basis points (10000 bps = 100%).
 *
 * These values are decided and are NOT placeholders. The table itself lives in
 * the shared catalog (single source of truth) and is re-exported here.
 */
export { SHIP_LEVEL_1_DAMAGE_BONUS_BPS };

/**
 * The bonus doubles with every upgrade, so level `L` is `base * 2^(L-1)`.
 *
 * Worst case is Prismatic at level 10: `3300 * 2^9 = 1_689_600` bps (+16896%).
 * That stays far inside `Number.MAX_SAFE_INTEGER`, so basis points remain a
 * plain integer here. The *damage* those basis points are applied to is a
 * `Fixed` bigint - see `@kenocyber/shared/fixed-point`.
 */
export function shipDamageBonusBps(rarity: Rarity, level: number): number {
  assertSafeIntegerInRange(level, 1, MAX_SHIP_LEVEL, 'ship level');
  return SHIP_LEVEL_1_DAMAGE_BONUS_BPS[rarity] * 2 ** (level - 1);
}

export interface EquippedShip {
  readonly rarity: Rarity;
  readonly level: number;
}

/**
 * Skins.
 *
 * Skins will grant a small extra percentage bonus. The exact numbers are NOT
 * decided - see `docs/OPEN_QUESTIONS.md`. The slot exists so nothing downstream
 * has to change when they are.
 */
export const SKIN_DAMAGE_BONUS_BPS_PENDING = null;
